import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';

import ReportDetail from './ReportDetail';
import Loader from 'components/Loader';
import { PATH_ROUTES } from 'routes/path';
import { PersonalisedAIReport } from 'services/personalised-ai-report/type';
import useAuthStore from 'store/auth';
import GenerateReport from './GenerateReport';
import PreviousReports from 'components/reports/PreviousReports';
import MobileReport from './MobileReport';
import { LoadMoreButton } from 'components/LoadMoreButton';
import { useInfiniteGetAIReports } from 'services/personalised-ai-report/hooks';

export default function PersonlisedAIReportPage() {
  const navigate = useNavigate();
  const { inView } = useInView();

  const profileId = useAuthStore((store) => store.currentUserProfileId);

  const {
    data: aiReports,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    hasNextPage,
  } = useInfiniteGetAIReports(profileId, 1);

  const [selectedTokenId, setSelectedTokenId] = useState<string>('');
  const [reportData, setReportData] = useState<PersonalisedAIReport[]>([]);

  useEffect(() => {
    if (aiReports?.length) {
      setReportData(aiReports);
      setSelectedTokenId(aiReports[0].token_id);
    } else {
      setReportData([]);
      setSelectedTokenId('');
    }

    if (aiReports && !aiReports.length) {
      navigate(PATH_ROUTES.GENERATE_AI_REPORT);
    }
  }, [aiReports]);

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView]);

  const scrollViewElement = (
    <div>
      {hasNextPage && !isFetchingNextPage ? (
        <LoadMoreButton
          onClick={() => fetchNextPage()}
          className='border-t-0'
        />
      ) : isFetchingNextPage ? (
        <Loader className='h-12' loaderSize='size-6' />
      ) : null}
    </div>
  );

  if (isLoading) return <Loader />;

  return (
    <div>
      <MobileReport
        reportData={reportData}
        setReportData={setReportData}
        scrollViewElement={scrollViewElement}
        isLoading={isLoading}
      />

      <div className='hidden px-6 pt-4 lg:block'>
        <div className='grid grid-cols-6 lg:grid-cols-10 xl:grid-cols-9 gap-x-8'>
          <div className='col-span-2 space-y-4 lg:col-span-3 xl:col-span-2'>
            <GenerateReport />

            <PreviousReports
              reportData={reportData}
              tokenId={selectedTokenId}
              setTokenId={setSelectedTokenId}
              setReportData={setReportData}
              scrollViewElement={scrollViewElement}
              isLoading={isLoading}
              reportType='ai'
            />
          </div>

          <div className='col-span-1 sm:col-span-8 lg:col-span-7 xl:col-span-7'>
            <ReportDetail reportTokenId={selectedTokenId} />
          </div>
        </div>
      </div>
    </div>
  );
}
